"use client";

import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import GooeyButton from "./GooeyButton";
import { useScroll, useMotionValueEvent } from "framer-motion";
import Link from "next/link";

export default function Hero() {
    const [touchedCardIndex, setTouchedCardIndex] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imagesRef = useRef<HTMLImageElement[]>([]);

    // total frames = 141
    const frameCount = 141;

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    useEffect(() => {
        // Preload images
        const loadImages = () => {
            const loadedImages: HTMLImageElement[] = [];
            for (let i = 1; i <= frameCount; i++) {
                const img = new window.Image();
                // ezgif-frame-001.jpg format
                const num = i.toString().padStart(3, "0");
                img.src = `/djsachin/ezgif-frame-${num}.jpg`;
                loadedImages.push(img);
            }
            imagesRef.current = loadedImages;

            // Draw first frame when the first image loads
            loadedImages[0].onload = () => {
                renderFrame(0);
            };
        };

        loadImages();
    }, []);

    const renderFrame = (index: number) => {
        if (!canvasRef.current || !imagesRef.current[index]) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const img = imagesRef.current[index];
        if (img.complete && img.naturalHeight !== 0) {
            // Set canvas size to match window size for highest quality
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;

            // Calculate 'object-fit: cover' logic
            const canvasRatio = canvas.width / canvas.height;
            const imgRatio = img.width / img.height;

            let drawWidth, drawHeight, offsetX, offsetY;

            if (canvasRatio > imgRatio) {
                drawWidth = canvas.width;
                drawHeight = canvas.width / imgRatio;
                offsetX = 0;
                offsetY = (canvas.height - drawHeight) / 2;
            } else {
                drawWidth = canvas.height * imgRatio;
                drawHeight = canvas.height;
                offsetX = (canvas.width - drawWidth) / 2;
                offsetY = 0;
            }

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        }
    };

    useMotionValueEvent(scrollYProgress, "change", (latest) => {
        const frameIndex = Math.min(
            frameCount - 1,
            Math.floor(latest * frameCount)
        );
        requestAnimationFrame(() => renderFrame(frameIndex));
    });

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const latest = scrollYProgress.get();
            const frameIndex = Math.min(
                frameCount - 1,
                Math.floor(latest * frameCount)
            );
            renderFrame(frameIndex);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [scrollYProgress]);

    const handleTouchStart = (index: number) => {
        setTouchedCardIndex(index);
    };

    const handleTouchEnd = () => {
        setTouchedCardIndex(null);
    };

    return (
        <div ref={containerRef} className="w-full h-[400vh] relative" id="home">
            <div className="sticky top-0 w-full h-screen overflow-hidden">
                {/* Canvas Background */}
                <canvas
                    ref={canvasRef}
                    className="absolute inset-0 w-full h-full object-cover z-0"
                />

                <div className="absolute inset-0 bg-gradient-to-r from-black/60 sm:from-black w-[40%] sm:w-[60%] to-transparent z-9" />
                <div className="flex flex-col justify-center h-full w-full relative z-10">
                    <div className="px-[5%] text-center sm:text-left mt-20 sm:mt-0">
                        <Link href="/" className="sm:block hidden w-fit">
                            <Image
                                src="/logo.webp"
                                alt="Logo"
                                width={300}
                                height={167}
                                draggable={false}
                            />
                        </Link>
                        <Image
                            src="/hero_text.svg"
                            alt="DJ Sachin Title"
                            width={300}
                            height={100}
                            className="w-full sm:w-[26vw] h-auto object-contain mx-auto sm:mx-0 sm:mt-[2vw]"
                            draggable={false}
                        />

                        {/* Paragraph */}
                        <h3 className=" text-white sm:mt-[1vw] text-sm sm:text-[1.1vw] mt-4">From soulful live melodies to high-energy DJ performances, <br className="sm:block hidden" />
                            <span className="text-[#FE3694]">DJ Sachin</span> creates unforgettable musical experiences for weddings, <br className="sm:block hidden" />
                            corporate events, and celebrations across Canada.
                        </h3>
                    </div>

                    {/* CTA Bottom Right */}
                    {/* <a
                        href="#form"
                        className="mx-auto lg:mx-0 bg-linear-to-t from-[#74EAF8] to-white hover:bg-transparent hover:p-0 rounded-full w-fit p-[2px] sm:p-[0.1vw] mt-6 sm:mt-0 sm:absolute right-[34vw] bottom-[2vh]"
                        style={{ fontFamily: "Himagsikan" }}
                    >
                        <GooeyButton
                            className="rounded-full w-full h-full block"
                            onTouchStart={() => handleTouchStart(0)}
                            onTouchEnd={handleTouchEnd}
                        >
                            <div className="px-10 lg:px-[2vw] py-3 lg:py-[0.6vw] text-2xl lg:text-[1.8vw]">
                                <p className="bg-linear-to-t from-[#74EAF8] to-white bg-clip-text text-transparent transition-all duration-300 group-hover:from-black group-hover:to-black">
                                    BOOK NOW
                                </p>
                            </div>
                        </GooeyButton>
                    </a> */}

                    {/* CTA Top Right */}
                    <a
                        href="#form"
                        className="sm:block hidden mx-auto lg:mx-0 bg-linear-to-t from-[#74EAF8] to-white hover:bg-transparent hover:p-0 rounded-full w-fit p-[2px] sm:p-[0.1vw] mt-6 sm:mt-0 sm:absolute right-[7.5vw] top-[18vh]"
                        style={{ fontFamily: "Himagsikan" }}
                    >
                        <GooeyButton
                            className="rounded-full w-full h-full block"
                            onTouchStart={() => handleTouchStart(0)}
                            onTouchEnd={handleTouchEnd}
                        >
                            <div className="px-10 lg:px-[2vw] py-3 lg:py-[0.6vw] text-2xl lg:text-[1.8vw]">
                                <p className="bg-linear-to-t from-[#74EAF8] to-white bg-clip-text text-transparent transition-all duration-300 group-hover:from-black group-hover:to-black">
                                    BOOK NOW
                                </p>
                            </div>
                        </GooeyButton>
                    </a>
                </div>
            </div>
        </div>
    );
}
